import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface LanguageSwitcherProps {
  variant?: 'icon' | 'full';
}

const languages = [
  { code: 'en', shortLabel: 'EN', nativeName: 'English' },
  { code: 'zh', shortLabel: '中文', nativeName: '中文' },
  { code: 'ja', shortLabel: 'JA', nativeName: '日本語' },
  { code: 'ko', shortLabel: 'KO', nativeName: '한국어' },
  { code: 'fr', shortLabel: 'FR', nativeName: 'Français' },
] as const;

export default function LanguageSwitcher({ variant = 'icon' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation('common');
  const { user, updateProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(language => language.code === i18n.language) ?? languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = async (next: string) => {
    await i18n.changeLanguage(next);
    localStorage.setItem('etherworks-language', next);
    setOpen(false);
    if (user) {
      await updateProfile({ preferences: { language: next } });
    }
  };

  const triggerClassName = variant === 'full'
    ? 'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-foreground bg-background border border-border hover:bg-accent/50 transition-colors'
    : 'px-2 py-1.5 text-sm font-medium rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors inline-flex items-center gap-1';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(previous => !previous)}
        className={triggerClassName}
        title={t('language.switch')}
      >
        <Globe className="w-4 h-4" />
        <span className={variant === 'full' ? '' : 'hidden sm:inline text-xs'}>
          {variant === 'full' ? currentLanguage.nativeName : currentLanguage.shortLabel}
        </span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-40 rounded-xl border border-border bg-background/95 shadow-lg backdrop-blur-sm p-1 z-50">
          {languages.map(language => {
            const active = language.code === currentLanguage.code;
            return (
              <button
                key={language.code}
                type="button"
                onClick={() => changeLanguage(language.code)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${active ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-accent/50'}`}
              >
                <span>{language.nativeName}</span>
                {active && <Check className="w-4 h-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
